/**
 * Lecture d'une erreur remontee par le client HTTP.
 *
 * `httpClient` leve un `HttpError` de React Admin — message **deja traduit** par
 * `apiError.ts`, statut HTTP conserve — ou un `SessionExpiredError` quand plus
 * aucune session n'est obtenable. Les ecrans d'administration ont besoin de deux
 * choses de cette erreur, et de deux seulement : la phrase a montrer, et la
 * distinction du **403**.
 *
 * Pourquoi le 403 merite sa propre fonction : c'est le seul statut qui ne
 * signale **ni une panne ni une session perdue**. L'utilisateur est authentifie,
 * son compte n'a simplement pas le role demande (ADR 0032). Le confondre avec
 * une erreur produirait un ecran rouge la ou il faut une explication ; le
 * confondre avec un 401 deconnecterait quelqu'un qui n'a rien fait de mal —
 * c'est la regle du dossier : **un 403 ne deconnecte jamais**.
 *
 * Aucune de ces fonctions n'utilise `instanceof` : la valeur traverse une
 * frontiere de donnees (une promesse rejetee est un `unknown`), et un
 * `instanceof` sur une classe importee depuis deux copies d'un paquet rend
 * `false` sans que rien ne le dise.
 */

/** Statut HTTP porte par l'erreur, ou `null` si elle n'en porte pas. */
export const httpStatusOf = (error: unknown): number | null => {
  if (typeof error !== 'object' || error === null) {
    return null;
  }

  const status: unknown = (error as { status?: unknown }).status;

  return typeof status === 'number' ? status : null;
};

/** Le serveur a repondu, et il a refuse : role insuffisant (ADR 0032). */
export const isForbidden = (error: unknown): boolean => httpStatusOf(error) === 403;

/** Le compte vise n'existe pas. */
export const isNotFound = (error: unknown): boolean => httpStatusOf(error) === 404;

/** Message generique, pose quand l'erreur n'en porte aucun d'exploitable. */
export const UNEXPECTED_ERROR_MESSAGE = 'Une erreur inattendue est survenue. Réessayez.';

/**
 * Phrase a afficher.
 *
 * Le message d'un `HttpError` vient de `messageForErrorCode` : il est en
 * francais et destine a l'utilisateur. On le prefere donc a toute reformulation
 * locale, qui divergerait de celle des autres ecrans.
 */
export const messageOf = (error: unknown): string => {
  if (typeof error === 'object' && error !== null) {
    const message: unknown = (error as { message?: unknown }).message;

    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
  }

  return UNEXPECTED_ERROR_MESSAGE;
};
